import React from 'react';
import { ThemeType } from 'src/theming/jssTypes';
import { Tabs } from 'src/design-system/components/atoms';
import { createUseStyles } from 'react-jss';
import { EllipsisOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'src/theming/useTheme';

const { TabPane } = Tabs;

const useStyles = createUseStyles((theme: ThemeType) => ({
  tabsContainer: {
    height: '48px',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    backgroundColor: theme.color.semantic.background.neutral.secondary,
    margin: theme.spacing[2],
    '& .ant-tabs-tab': {
      '& $metricValue': {
        color: theme.color.semantic.text.inverse,
        backgroundColor: theme.color.semantic.text.soft,
      },
    },
    '& .ant-tabs-tab:hover': {
      '& $metricValue': {
        backgroundColor: theme.color.semantic.brand.default.secondary,
      },
    },
    '& .ant-tabs-tab-active': {
      '& $metricValue': {
        backgroundColor: `${theme.color.semantic.brand.default.primary} !important`,
      },
    },
    '& .ant-tabs-tab-active:hover': {
      '& $metricValue': {
        backgroundColor: theme.color.semantic.brand.default.primary,
      },
    },
    '& .ant-tabs-content': {
      display: 'flex',
      flexDirection: 'column',
      flex: 1,
      height: '0',
    },
  },
  tabPane: {
    height: '0',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  metricValue: {
    fontSize: theme.typography.fontSize.xs,
    lineHeight: theme.typography.lineHeight.tight,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.color.semantic.text.inverse,
    backgroundColor: theme.color.semantic.background.neutral.inverse,
    padding: `${theme.spacing[1]} ${theme.spacing[2]}`,
    borderRadius: theme.radius.lg,
    marginLeft: theme.spacing[2],
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    flex: 1,
  },
  tabDataContainer: {
    height: 'calc(100% - 48px - 16px)',
    display: 'flex',
    flexDirection: 'column',
  },
  setFontSizeBodyText: {
    fontSize: theme.typography.fontSize.sm,
  },
}));

export interface Tab {
  header: {
    title: string;
    metric?: string;
    customMetricNode?: React.ReactNode;
    icon?: React.ReactNode;
  };
  key: string;
  Component: React.ReactNode;
  disabled?: boolean;
}

export interface GenericTabsWithSummaryMetricProps {
  tabs: Tab[];
  defaultActiveKey: string | null;
  onChange: ((key: string) => void) | null;
  activeTab?: any;
  destroyInactiveTabPane?: boolean;
  mode?: string;
  type?: string;
  [key: string]: any;
}

type Props = GenericTabsWithSummaryMetricProps & RouteComponentProps;

const GenericTabsWithSummaryMetric: React.FunctionComponent<Props> = ({
  tabs = [],
  defaultActiveKey,
  onChange,
  classes: _classes,
  destroyInactiveTabPane,
  activeTab: activeTabFromProps,
  match,
  staticContext,
  mode,
  type,
  ...props
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = React.useState<string | null>(
    activeTabFromProps || defaultActiveKey || tabs[0]?.key,
  );

  const renderMetric = (metric?: string) => {
    const numericMetric = metric ? parseInt(metric, 10) : undefined;
    const finalMetric =
      numericMetric && numericMetric > 9999 ? '9999+' : metric;
    return metric ? (
      <span className={classNames(classes.metricValue)}>{t(finalMetric)}</span>
    ) : (
      <></>
    );
  };

  React.useEffect(() => {
    const newTab = activeTabFromProps || defaultActiveKey || tabs[0]?.key;
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [activeTabFromProps]);

  const renderTabHeader = (header: Tab['header']) => {
    const { title, customMetricNode, metric, icon } = header;

    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {icon ? <span style={{ marginRight: theme.spacing[2] }}>{icon}</span> : <></>}
        {t(title)}
        {customMetricNode || renderMetric(metric)}
      </div>
    );
  };

  const renderActiveComponent = () => {
    const pathname = match?.path ?? window.location.pathname;
    const activeTabData = tabs.find((tab) => tab.key === activeTab);
    const id = `__tab-generic-shipsy-${pathname}:${activeTab}`;
    return (
      <div
        className={classNames(
          classes.tabDataContainer,
          'shipsy-generic-tabs-with-sm-cls',
        )}
        id={id}
      >
        {activeTabData?.Component ?? <></>}
      </div>
    );
  };

  const handleChange = (key: string) => {
    setActiveTab(key);
    if (onChange) {
      onChange(key);
    }
  };

  return (
    <div className={classes.container}>
      <Tabs
        activeKey={activeTab}
        moreIcon={<EllipsisOutlined className={classes.setFontSizeBodyText} />}
        onChange={handleChange}
        className={classes.tabsContainer}
        tabBarStyle={{ paddingRight: theme.spacing[1] }}
        {...props}
      >
        {tabs.map(({ header, key, disabled }, index) => {
          return (
            <TabPane
              className={classNames(classes.tabPane)}
              tab={renderTabHeader(header)}
              key={key || index + 1}
              disabled={disabled}
            >
              <></>
            </TabPane>
          );
        })}
      </Tabs>
      {renderActiveComponent()}
    </div>
  );
};

export default withRouter(GenericTabsWithSummaryMetric);
